import classNames from 'classnames'
import IconButton from 'components/common/iconButton/IconButton'
import IconRender from 'components/common/IconRender'
import Loading from 'components/common/loading/Loading'
import NoImageSample from 'components/common/NoImageSample'
import ShareUnitTemplates from 'components/myAccount/ShareUnitTemplates'
import { formatListingAvailabilityLabel } from 'components/propertyCard/formatListingAvailability'
import { formatListingPrice } from 'components/propertyCard/formatListingPrice'
import ListingAddressRow from 'components/propertyCard/ListingAddressRow'
import {
  isListingUnavailable,
  listingCardHighlights,
} from 'components/propertyCard/listingCardHighlights'
import {
  listingRoomLabel,
  listingTypeLabel,
} from 'components/propertyCard/listingCardMeta'
import PropertySharePopup from 'components/propertyCard/PropertySharePopup'
import { Unit } from 'newTypes/unit'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'
import { VerificationStatus } from 'types/property'

import ListingVerificationBadge from './ListingVerificationBadge'

interface ListItemProps {
  className?: string
  property: Unit
  currentIndex: number | null
  handleClick: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  handleFavorite?: (propertyId: string) => void
  loadingFavoriteId?: string | null
  handleVerify?: () => void
}

const actionBtnClass =
  'relative flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imovPrimary'

const ListItem = ({
  className,
  property,
  currentIndex,
  loadingFavoriteId,
  handleClick,
  handleFavorite,
  handleVerify,
}: ListItemProps) => {
  const { t, i18n } = useTranslation()
  const [isShareOpen, setIsShareOpen] = useState(false)
  const [isHoveringVerify, setIsHoveringVerify] = useState(false)
  const shareAnchorRef = useRef<HTMLDivElement>(null)

  const title = property.title || 'Cozy Apartment - Lease Transfer'
  const formattedPrice = formatListingPrice(property.price, property.currency)
  const roomLabel = listingRoomLabel(t, property.bedrooms)
  const leaseLabel = listingTypeLabel(t, property)
  const unavailable = isListingUnavailable(property as Parameters<typeof isListingUnavailable>[0])
  const availabilityLabel = formatListingAvailabilityLabel(
    t,
    property.availability,
    i18n.language || 'fr-CA'
  )
  const highlights = useMemo(
    () => listingCardHighlights(t, property as unknown as Record<string, unknown>),
    [t, property]
  )
  const favoriteBusy = loadingFavoriteId === property.id

  const openListing = () => {
    if (unavailable) return
    handleClick()
  }

  return (
    <>
      <div
        key={`list-item-${currentIndex}`}
        role="link"
        tabIndex={unavailable ? -1 : 0}
        aria-label={`${formattedPrice} · ${roomLabel}`}
        onClick={openListing}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openListing()
          }
        }}
        className={classNames(
          'relative flex w-full min-w-0 gap-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200',
          unavailable
            ? 'cursor-not-allowed opacity-75'
            : 'cursor-pointer hover:-translate-y-0.5 hover:border-imovPrimary/30 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imovPrimary',
          className
        )}
      >
        <div className="relative h-44 w-40 shrink-0 sm:h-48 sm:w-44">
          <div className="absolute inset-0 bg-gray-100">
            {property.images[0] ? (
              <img
                src={property.images[0]}
                alt={title}
                className="h-full w-full object-cover"
              />
            ) : (
              <NoImageSample />
            )}
          </div>

          {unavailable ? (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/55 px-2 text-center">
              <p className="rounded-full bg-white/95 px-2 py-1 text-xs font-semibold text-imovSecondary">
                {t('propertyCard.unavailable')}
              </p>
            </div>
          ) : null}

          <div className="absolute left-3 top-3 z-20 flex max-w-[calc(100%-3.5rem)] items-center gap-1.5">
            {property.verified &&
            (handleVerify || property.verified === VerificationStatus.Approved) ? (
              <ListingVerificationBadge verified={property.verified} />
            ) : null}
            <span className="inline-flex min-w-0 truncate rounded-md bg-white/95 px-2 py-0.5 text-[11px] font-medium tracking-wide text-gray-600 shadow-sm ring-1 ring-black/5">
              {leaseLabel}
            </span>
          </div>

          <div ref={shareAnchorRef} className="absolute bottom-3 left-3 z-10 flex h-9 w-9">
            <IconButton
              icon="regular-share-nodes"
              size={IconSizes.LG}
              color="#111827"
              handleClick={e => {
                e.stopPropagation()
                e.preventDefault()
                setIsShareOpen(true)
              }}
              containerClassName={actionBtnClass}
            />
          </div>

          {handleFavorite ? (
            <div className="absolute right-3 top-3 z-10">
              {favoriteBusy ? (
                <Loading className="!p-0 h-9 w-9 rounded-full bg-white/90 shadow-sm" />
              ) : (
                <IconButton
                  icon={property.isFavorited ? 'solid-heart' : 'regular-heart'}
                  size={IconSizes.LG}
                  color={
                    property.isFavorited ? CustomColorsRef.Primary : '#111827'
                  }
                  handleClick={e => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (favoriteBusy) return
                    handleFavorite?.(property.id)
                  }}
                  containerClassName={actionBtnClass}
                />
              )}
            </div>
          ) : null}
        </div>

        <div className="flex min-h-[11rem] min-w-0 flex-1 items-stretch gap-2 py-2.5 pl-3 pr-2 sm:min-h-[12rem] sm:gap-3 sm:py-3 sm:pr-3">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
            <div className="flex min-w-0 items-start justify-between gap-4">
              <p className="min-w-0 text-2xl font-bold leading-tight text-imovSecondary">
                {formattedPrice}
              </p>
              <p className="shrink-0 pt-1 text-lg font-semibold leading-none text-gray-800">
                {roomLabel}
              </p>
            </div>

            <p className="flex items-center gap-1.5 text-sm font-medium text-imovPrimary">
              <IconRender
                icon="regular-calendar"
                size={IconSizes.SM}
                color={CustomColorsRef.Primary}
              />
              <span>{availabilityLabel}</span>
            </p>

            <ListingAddressRow
              property={property}
              fallback={t('propertyCard.addressUnavailable', 'Address not provided')}
            />

            {highlights.length > 0 ? (
              <p className="text-xs leading-relaxed text-gray-600">
                {highlights.join(' · ')}
              </p>
            ) : null}

            {(!property.verified ||
              property.verified === VerificationStatus.Rejected) &&
              handleVerify ? (
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  className="flex max-w-full rounded-md border border-imovPrimaryDark bg-imovWhite px-2 py-1 text-xs text-imovPrimaryDark hover:border-imovPrimary hover:text-imovPrimary sm:max-w-[11rem]"
                  onClick={e => {
                    e.stopPropagation()
                    handleVerify()
                  }}
                  onMouseEnter={() => setIsHoveringVerify(true)}
                  onMouseLeave={() => setIsHoveringVerify(false)}
                >
                  <IconRender
                    icon="regular-shield-exclamation"
                    color={
                      isHoveringVerify
                        ? CustomColorsRef.Primary
                        : CustomColorsRef.PrimaryDark
                    }
                    size={IconSizes.MD}
                    className="mr-1 shrink-0"
                  />
                  Start Verification
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {handleVerify ? (
        <ShareUnitTemplates
          isOpen={isShareOpen}
          handleClose={() => setIsShareOpen(false)}
          unit={property}
        />
      ) : (
        <PropertySharePopup
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          shareUrl={`${window.location.origin}/listing/${property.id}`}
          anchorRef={shareAnchorRef}
        />
      )}
    </>
  )
}

export default ListItem
